import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteLayout } from "@/components/site/Layout";
import { useContent, useSeo } from "@/context/ContentContext";

const Step = ({ i, t }) => {
  const flip = i % 2 === 0;
  return (
    <section className="grid lg:grid-cols-[1fr_1.35fr] gap-10 items-center py-14 border-t border-border first:border-0" data-testid={`tour-step-${i}`}>
      <div className={`space-y-4 ${flip ? "lg:order-2" : ""}`}>
        <span className="inline-flex items-center gap-2">
          <span className="h-8 w-8 rounded-full bg-primary text-white font-display font-bold text-sm flex items-center justify-center">{i}</span>
          <span className="eyebrow">{t[`step${i}_tag`]}</span>
        </span>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid={`tour-step-${i}-title`}>{t[`step${i}_title`]}</h2>
        <p className="text-slate-600 leading-relaxed" data-testid={`tour-step-${i}-text`}>{t[`step${i}_text`]}</p>
      </div>
      <div className={`rounded-[1.5rem] border border-border bg-card p-2 shadow-xl lift ${flip ? "lg:order-1" : ""}`}>
        <div className="flex gap-1.5 px-3 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-300" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
        </div>
        <img src={t[`step${i}_image`]} alt={t[`step${i}_title`]} loading="lazy" className="w-full rounded-xl border border-border object-cover object-top aspect-[16/10]" data-testid={`tour-step-${i}-image`} />
      </div>
    </section>
  );
};

export default function Tour() {
  useSeo("tour");
  const { tour: t = {} } = useContent();
  const steps = Object.keys(t).filter((k) => /^step\d+_title$/.test(k)).map((k) => Number(k.match(/\d+/)[0])).sort((a, b) => a - b);
  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="max-w-2xl fade-up">
          <p className="eyebrow">{t.tour_eyebrow}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2" data-testid="tour-title">{t.tour_title}</h1>
          <p className="text-base sm:text-lg text-slate-600 mt-4" data-testid="tour-subtitle">{t.tour_subtitle}</p>
        </div>
        <div className="mt-8">{steps.map((i) => <Step key={i} i={i} t={t} />)}</div>
        <div className="rounded-[2rem] bg-[#1E3A34] text-white p-10 text-center mt-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{t.tour_cta ? "Ready when you are" : ""}</h2>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Button className="rounded-full bg-accent hover:bg-accent/90 px-7 h-12" asChild data-testid="tour-cta-button"><Link to="/for-clinics/signup">{t.tour_cta} <ArrowRight className="h-4 w-4 ml-2" /></Link></Button>
            <Button variant="outline" className="rounded-full px-7 h-12 bg-transparent text-white border-white/30 hover:bg-white/10 hover:text-white" asChild data-testid="tour-pricing-button"><Link to="/pricing">See pricing</Link></Button>
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}
